// Settings screen — Users CRUD UI for V2.1 auth (P-046 / D-141) +
// Constant Contact connection surface for V2.2 Push (P-047 / D-142).
//
// **Two sections.** "Constant Contact" sits above "Users" per Tech Arch
// v2-cc-push §4. CC section shows the per-browser connection state +
// Connect / Disconnect buttons; Users section is unchanged from P-046.
// Q3 hardcode (Banks-confirmed at P-047 session opening) keeps the
// from-email + from-name out of this UI — those constants live with
// the API helper at P-048.
//
// **Three modals reusing the shared `<Modal>` scaffold (P-032).** Add
// User and Reset Password share a credential-form shape (email-disabled
// vs editable + password input + submit + inline error); Remove uses
// the existing `<ConfirmModal>` with the Q2 explicit-honest copy
// Banks-confirmed at session opening — calls out the no-revocation
// trade-off so chapter staff understand operational implications. The
// CC Disconnect button reuses `<ConfirmModal>` directly with shorter
// copy (no per-user destructive semantics — just clears the chapter's
// per-browser CC tokens).
//
// **Cannot remove self guard (UI layer).** The current user's row has
// `disabled` + a `title` tooltip on Remove. The data layer
// (`auth.deleteUser`) is intentionally unconstrained so a future
// admin tool can remove any account if needed.
//
// **Dynamic-imports.** auth.ts (Firestore SDK + bcryptjs) AND
// cc-oauth.ts (no SDK; just fetch + browser crypto) both load lazily
// at their respective interaction seams. Static imports would pull
// both chunks into the initial editor bundle.

import { useEffect, useRef, useState } from 'preact/hooks'

import type { UserSummary } from '../../lib/auth'
import { MIN_PASSWORD_LENGTH } from '../../lib/auth'
import { useCurrentSession } from '../auth-gate'
import { secondaryButtonClassCompactSmWithDisabled } from '../button-classes'
import { ConfirmModal } from '../confirm-modal'
import { Modal } from '../modal'

const CANNOT_REMOVE_SELF_TOOLTIP = "You can't remove your own account while signed in."

const REMOVE_CONFIRM_COPY = (email: string) =>
  `Permanently remove ${email}? They can no longer sign in. Any existing sessions they have in other browsers stay active until they sign out or close the tab.`

const inputClass =
  'block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-ypo-navy focus:outline-none focus:ring-1 focus:ring-ypo-navy disabled:bg-gray-50'

const primaryButtonClass =
  'rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep disabled:cursor-not-allowed disabled:opacity-60'

// Row buttons + modal Cancel buttons share the existing compact-sm shared
// class. Identical class string to the local equivalent that the first
// draft of this file inlined; the shared constant pre-exists in
// button-classes.ts (P-037.30 4th-batch §B and earlier consumers).
const secondaryRowButtonClass = secondaryButtonClassCompactSmWithDisabled

function formatCreatedAt(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function SettingsScreen() {
  const session = useCurrentSession()
  const [users, setUsers] = useState<readonly UserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [resetTarget, setResetTarget] = useState<UserSummary | null>(null)
  const [removeTarget, setRemoveTarget] = useState<UserSummary | null>(null)

  const refresh = async () => {
    setLoadError(null)
    setLoading(true)
    try {
      const { listUsers } = await import('../../lib/auth')
      const next = await listUsers()
      setUsers([...next].sort((a, b) => a.email.localeCompare(b.email)))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Couldn't load users.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return
    try {
      const { deleteUser } = await import('../../lib/auth')
      await deleteUser(removeTarget.email)
      setRemoveTarget(null)
      void refresh()
    } catch (err) {
      // Surface failure inline in the (closing) confirm? Simpler: keep
      // it open with the message swapped. For V1, close + use the page
      // load-error banner instead — the list refresh will show the
      // unchanged row, and the user can re-attempt.
      setRemoveTarget(null)
      setLoadError(err instanceof Error ? err.message : "Couldn't remove user.")
    }
  }

  return (
    <div class="space-y-6">
      <ConstantContactSection />

      <section class="space-y-3">
        <header class="flex flex-wrap items-baseline justify-between gap-2">
          <h2 class="m-0 text-lg font-semibold">Users</h2>
          <button type="button" onClick={() => setAddOpen(true)} class={primaryButtonClass}>
            + Add User
          </button>
        </header>

        {loadError !== null && (
          <p
            role="alert"
            class="m-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {loadError}
          </p>
        )}

        {loading ? (
          <p class="m-0 text-sm text-gray-600">Loading users…</p>
        ) : users.length === 0 ? (
          <p class="m-0 text-sm text-gray-600">
            No users yet — click "Add User" to create the first one.
          </p>
        ) : (
          <ul class="m-0 list-none divide-y divide-gray-200 border border-gray-200 p-0">
            {users.map((u) => {
              const isSelf = u.email === session.email
              return (
                <li key={u.email} class="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium text-gray-900">{u.email}</div>
                    <div class="text-xs text-gray-500">Added {formatCreatedAt(u.createdAt)}</div>
                  </div>
                  <div class="flex flex-wrap items-baseline gap-2">
                    <button
                      type="button"
                      onClick={() => setResetTarget(u)}
                      class={secondaryRowButtonClass}
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(u)}
                      disabled={isSelf}
                      title={isSelf ? CANNOT_REMOVE_SELF_TOOLTIP : undefined}
                      class={secondaryRowButtonClass}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <AddUserModal
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onSuccess={() => {
          setAddOpen(false)
          void refresh()
        }}
      />
      <ResetPasswordModal
        target={resetTarget}
        onCancel={() => setResetTarget(null)}
        onSuccess={() => setResetTarget(null)}
      />
      <ConfirmModal
        open={removeTarget !== null}
        title="Remove user"
        message={removeTarget !== null ? REMOVE_CONFIRM_COPY(removeTarget.email) : ''}
        confirmLabel="Remove"
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  )
}

const CC_DISCONNECT_CONFIRM_COPY =
  'Disconnect Constant Contact for this browser? Anyone signed in here will need to reconnect before pushing emails to CC.'

function ConstantContactSection() {
  // `null` = haven't checked yet (synchronous read on mount).
  const [connected, setConnected] = useState<boolean | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const { isConnected } = await import('../../lib/cc-oauth')
      if (!cancelled) setConnected(isConnected())
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleConnect = async () => {
    setError(null)
    setBusy(true)
    try {
      const { startOAuth } = await import('../../lib/cc-oauth')
      await startOAuth()
      // startOAuth navigates the top-level window away to CC's authorize
      // endpoint; if we get here, navigation hasn't happened yet (the
      // happens-after path) — leave `busy` true so the button stays
      // disabled while the navigation finishes.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start the Constant Contact connection.")
      setBusy(false)
    }
  }

  const handleDisconnectConfirm = async () => {
    setError(null)
    setBusy(true)
    try {
      const { disconnect, isConnected } = await import('../../lib/cc-oauth')
      await disconnect()
      setConnected(isConnected())
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't disconnect from Constant Contact.")
    } finally {
      setBusy(false)
      setConfirmDisconnect(false)
    }
  }

  return (
    <section class="space-y-3" aria-labelledby="cc-section-heading">
      <header class="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="cc-section-heading" class="m-0 text-lg font-semibold">
          Constant Contact
        </h2>
      </header>
      <p class="m-0 text-sm text-gray-600">
        Connect once per browser. Anyone signed in here can push emails directly to the chapter's
        Constant Contact account.
      </p>
      {error !== null && (
        <p
          role="alert"
          class="m-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      <div class="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 px-4 py-3">
        <div class="text-sm">
          <span class="font-medium text-gray-900">Status:</span>{' '}
          {connected === null ? (
            <span class="text-gray-500">Checking…</span>
          ) : connected ? (
            <span class="text-green-700">Connected</span>
          ) : (
            <span class="text-gray-700">Not connected</span>
          )}
        </div>
        <div class="flex flex-wrap items-baseline gap-2">
          {connected === true ? (
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
              class={secondaryRowButtonClass}
            >
              Disconnect
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={busy || connected === null}
              class={primaryButtonClass}
            >
              {busy ? 'Connecting…' : 'Connect to Constant Contact'}
            </button>
          )}
        </div>
      </div>
      <ConfirmModal
        open={confirmDisconnect}
        title="Disconnect Constant Contact"
        message={CC_DISCONNECT_CONFIRM_COPY}
        confirmLabel="Disconnect"
        onConfirm={handleDisconnectConfirm}
        onCancel={() => setConfirmDisconnect(false)}
      />
    </section>
  )
}

interface AddUserModalProps {
  readonly open: boolean
  readonly onCancel: () => void
  readonly onSuccess: () => void
}

function AddUserModal({ open, onCancel, onSuccess }: AddUserModalProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const emailRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) {
      setEmail('')
      setPassword('')
      setError(null)
      setSubmitting(false)
      return
    }
    emailRef.current?.focus()
  }, [open])

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()
    if (submitting) return
    setError(null)
    setSubmitting(true)
    try {
      const { createUser } = await import('../../lib/auth')
      await createUser(email, password)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add user.")
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onCancel={onCancel} labelledBy="add-user-title">
      <h2 id="add-user-title" class="m-0 mb-1 text-lg font-semibold text-gray-900">
        Add user
      </h2>
      <p class="m-0 mb-4 text-sm text-gray-600">
        New users can sign in immediately with these credentials.
      </p>
      <form onSubmit={handleSubmit}>
        <label class="mb-3 block">
          <span class="mb-1 block text-sm font-medium text-gray-800">Email</span>
          <input
            ref={emailRef}
            type="email"
            name="email"
            autoComplete="off"
            required
            value={email}
            onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
            disabled={submitting}
            class={inputClass}
          />
        </label>
        <label class="mb-4 block">
          <span class="mb-1 block text-sm font-medium text-gray-800">Password</span>
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)}
            disabled={submitting}
            class={inputClass}
          />
          <span class="mt-1 block text-xs text-gray-500">
            Minimum {MIN_PASSWORD_LENGTH} characters.
          </span>
        </label>
        {error !== null && (
          <p
            role="alert"
            class="m-0 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {error}
          </p>
        )}
        <div class="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            class={secondaryRowButtonClass}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} class={primaryButtonClass}>
            {submitting ? 'Adding…' : 'Add user'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

interface ResetPasswordModalProps {
  readonly target: UserSummary | null
  readonly onCancel: () => void
  readonly onSuccess: () => void
}

function ResetPasswordModal({ target, onCancel, onSuccess }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const open = target !== null

  useEffect(() => {
    if (!open) {
      setPassword('')
      setError(null)
      setSubmitting(false)
      return
    }
    passwordRef.current?.focus()
  }, [open])

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()
    if (submitting || target === null) return
    setError(null)
    setSubmitting(true)
    try {
      const { updateUserPassword } = await import('../../lib/auth')
      await updateUserPassword(target.email, password)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't reset password.")
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onCancel={onCancel} labelledBy="reset-password-title">
      <h2 id="reset-password-title" class="m-0 mb-1 text-lg font-semibold text-gray-900">
        Reset password
      </h2>
      <p class="m-0 mb-4 text-sm text-gray-600">
        Set a new password for <span class="font-medium text-gray-900">{target?.email}</span>.
        They'll need to sign in with the new password from any new browser session.
      </p>
      <form onSubmit={handleSubmit}>
        <label class="mb-4 block">
          <span class="mb-1 block text-sm font-medium text-gray-800">New password</span>
          <input
            ref={passwordRef}
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)}
            disabled={submitting}
            class={inputClass}
          />
          <span class="mt-1 block text-xs text-gray-500">
            Minimum {MIN_PASSWORD_LENGTH} characters.
          </span>
        </label>
        {error !== null && (
          <p
            role="alert"
            class="m-0 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {error}
          </p>
        )}
        <div class="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            class={secondaryRowButtonClass}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} class={primaryButtonClass}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
